import { useEffect, useState } from "react";

/**
 * Top-of-page reading progress bar — fires its `scroll` listener once via
 * `requestAnimationFrame`, so it stays cheap on long content pages
 * (Routine results, SEO ingredient/product/medication pages).
 *
 * Hides itself on short pages (<200px scroll runway) and at the very top
 * (avoids a flicker before the user starts reading).
 */
export default function ReadingProgressBar({ tone = "ink" }) {
  const [pct, setPct] = useState(0);
  const [active, setActive] = useState(false);

  useEffect(() => {
    let ticking = false;
    const compute = () => {
      const doc = document.documentElement;
      const runway = doc.scrollHeight - doc.clientHeight;
      if (runway < 200) {
        setActive(false);
        setPct(0);
        return;
      }
      const next = Math.max(0, Math.min(100, (window.scrollY / runway) * 100));
      setPct(next);
      setActive(window.scrollY > 80);
    };
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        compute();
        ticking = false;
      });
    };
    compute();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", compute);
    };
  }, []);

  const barColor =
    tone === "peach" ? "bg-peach-600"
    : tone === "amber" ? "bg-amber-500"
    : "bg-ink";

  return (
    <div
      data-testid="reading-progress-bar"
      aria-hidden="true"
      className="pointer-events-none fixed top-0 left-0 right-0 h-[3px] z-[60]"
      style={{ opacity: active ? 1 : 0, transition: "opacity 200ms ease-out" }}
    >
      <div
        className={`h-full ${barColor} transition-[width] duration-150 ease-out`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}
